import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { RouterLinkAs } from '@/lib/router-link';
import {
  Body, Card, Eyebrow, Label, PageHeader, ScreenBody, Stack, TextLink, } from '@leanin/ui';

const SUPPORT_EMAIL = 'info@leanin.org';
const TRUST_EMAIL = 'trust@leanin.org';

type Faq = {
  readonly q: string;
  readonly a: ReactNode;
};

const FAQS: readonly Faq[] = [
  {
    q: "I can't sign in. What now?",
    a: (
      <>
        Try a password reset from the{' '}
        <TextLink href="/sign-in" linkAs={RouterLinkAs}>sign-in screen</TextLink>. If you signed up with SSO
        (Google, your work account), use the same provider you used the first
        time — mixing them creates a separate account. Still stuck?
        Email{' '}
        <TextLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</TextLink> with the
        address on your account.
      </>
    ),
  },
  {
    q: 'How do I change my photo or display name?',
    a: (
      <>
        Open{' '}
        <TextLink href="/settings/account" linkAs={RouterLinkAs}>Settings → Profile and identity</TextLink>.
        Upload a new photo from the avatar control, edit your name and bio,
        and save. Changes appear across the community within a minute.
      </>
    ),
  },
  {
    q: 'Who can see my profile and Circles?',
    a: (
      <>
        New accounts default to private — your profile is visible to fellow
        members of Circles and Networks you join. Adjust visibility in{' '}
        <TextLink href="/settings/privacy" linkAs={RouterLinkAs}>Settings → Privacy and visibility</TextLink>.
        Messages are always visible only to the people in the conversation.
      </>
    ),
  },
  {
    q: 'How do I deactivate my account?',
    a: (
      <>
        Go to{' '}
        <TextLink href="/settings/account" linkAs={RouterLinkAs}>Settings → Profile and identity</TextLink>{' '}
        and use "Deactivate my account" at the bottom. Your profile,
        posts, and Circle memberships are hidden immediately. Sign back in
        within 30 days to restore everything; after 30 days the account is
        permanently deleted.
      </>
    ),
  },
  {
    q: 'How do I export my data?',
    a: (
      <>
        Use{' '}
        <TextLink href="/settings/dsr" linkAs={RouterLinkAs}>Settings → Data and downloads</TextLink> to file
        an access or portability request. We respond within 30 days; most
        exports are ready in a few business days. The same page handles
        rectification, erasure, restriction, and objection requests under
        GDPR and similar laws.
      </>
    ),
  },
];

function HelpPage() {
  return (
    <ScreenBody gap="2xl">
      <PageHeader
        eyebrow="Help"
        title="Help and support"
        subtitle="Member support, policies, and community guidelines."
        back={{ label: 'Settings', href: '/settings' }}
      />

      <Stack gap="md">
        <Eyebrow>Member support</Eyebrow>
        <Card pad="default">
          <Stack gap="md">
            <Body>
              Email{' '}
              <TextLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</TextLink>{' '}
              for account questions, technical issues, accessibility requests,
              or anything you can't find in settings. Include the email address
              on your account and a brief description — screenshots help when
              something looks broken.
            </Body>
          </Stack>
        </Card>
      </Stack>

      <Stack gap="md">
        <Eyebrow>Policies</Eyebrow>
        <Card pad="default">
          <Stack gap="sm">
            <TextLink href="/terms" linkAs={RouterLinkAs}>
              Terms of Service
            </TextLink>
            <TextLink href="/privacy" linkAs={RouterLinkAs}>
              Privacy Policy
            </TextLink>
            <TextLink href="/community-guidelines" linkAs={RouterLinkAs}>
              Community Guidelines
            </TextLink>
          </Stack>
        </Card>
      </Stack>

      <Stack gap="md">
        <Eyebrow>Trust and safety</Eyebrow>
        <Card pad="default">
          <Stack gap="md">
            <Body>
              See a post, comment, message, or member that violates the
              guidelines? Use the report control on the item itself — the
              "more" menu on any post or member card includes a{' '}
              <strong>Report</strong> option. Reports go to a human moderator
              and are never shared with the person you reported.
            </Body>
            <Body>
              For urgent or sensitive concerns — threats, doxxing,
              suspected impersonation — email{' '}
              <TextLink href={`mailto:${TRUST_EMAIL}`}>{TRUST_EMAIL}</TextLink>. The trust
              team triages within 24 hours and can take action without you
              being identified to the other party.
            </Body>
            <Body size="sm" color="mute">
              If a post of yours has been hidden pending review, you'll
              find the contestation flow linked from the notice we send you.
            </Body>
          </Stack>
        </Card>
      </Stack>

      <Stack gap="md">
        <Eyebrow>Frequently asked</Eyebrow>
        <Card pad="default">
          <Stack gap="xl">
            {FAQS.map((f) => (
              <Stack key={f.q} gap="xs">
                <Label>{f.q}</Label>
                <Body color="mute">{f.a}</Body>
              </Stack>
            ))}
          </Stack>
        </Card>
      </Stack>
    </ScreenBody>
  );
}

export const Route = createFileRoute('/help')({
  component: HelpPage,
});
